import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, FileQuestion } from 'lucide-react'
import { DocsLayout } from '@/components/docs/DocsLayout'
import { DocsContent } from '@/components/docs/DocsContent'
import { useDocs, getAllDocsForSearch } from '@/hooks/useDocs'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import type { DocsPageMeta } from '@/types/docs'

/**
 * Main documentation page component
 * Handles routing, content loading, and layout
 */
export function DocsPage() {
  const { '*': pathParam } = useParams()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const [searchIndex, setSearchIndex] = useState<DocsPageMeta[]>([])

  // Default to getting-started/index if no path specified
  const docPath = pathParam || 'getting-started/index'

  // Build base path for navigation
  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view/docs`
    : '/dashboard/docs'

  // Build back link
  const backLink = {
    href: isImpersonating
      ? `/admin/accounts/${impersonatedAccount?.slug}/view/dashboard`
      : '/dashboard',
    label: 'Back to Dashboard',
  }

  const { content, isLoading, error, pageTitle, section, prevPage, nextPage } = useDocs(docPath)

  // Load search index
  useEffect(() => {
    getAllDocsForSearch().then(setSearchIndex)
  }, [])

  return (
    <DocsLayout
      currentPath={docPath}
      basePath={basePath}
      searchIndex={searchIndex}
      backLink={backLink}
    >
      {/* Loading state */}
      {isLoading && <DocsLoadingSkeleton />}

      {/* Error state */}
      {error && <DocsErrorState error={error} basePath={basePath} />}

      {/* Content */}
      {!isLoading && !error && content && (
        <>
          <DocsContent content={content} />

          {/* Page navigation */}
          <nav className="mt-12 pt-8 border-t flex justify-between gap-4">
            {prevPage ? (
              <Link
                to={`${basePath}/${prevPage.path}`}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
              >
                <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Previous</div>
                  <div className="font-medium text-foreground">{prevPage.title}</div>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {nextPage && (
              <Link
                to={`${basePath}/${nextPage.path}`}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group text-right"
              >
                <div>
                  <div className="text-xs text-muted-foreground">Next</div>
                  <div className="font-medium text-foreground">{nextPage.title}</div>
                </div>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            )}
          </nav>
        </>
      )}
    </DocsLayout>
  )
}

/**
 * Loading skeleton for documentation content
 */
function DocsLoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* Title */}
      <Skeleton className="h-10 w-2/3" />

      {/* Paragraphs */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
      </div>

      {/* Heading */}
      <Skeleton className="h-8 w-1/2 mt-8" />

      {/* More paragraphs */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>

      {/* Image placeholder */}
      <Skeleton className="h-64 w-full rounded-lg mt-6" />

      {/* More content */}
      <div className="space-y-3 mt-6">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    </div>
  )
}

/**
 * Error state when documentation page is not found
 */
function DocsErrorState({ error, basePath }: { error: string; basePath: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <FileQuestion className="h-16 w-16 text-muted-foreground mb-4" />
      <h1 className="text-2xl font-semibold mb-2">Page Not Found</h1>
      <p className="text-muted-foreground mb-6 max-w-md">
        The documentation page you're looking for doesn't exist or may have been moved.
      </p>
      <Button asChild>
        <Link to={basePath}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Documentation Home
        </Link>
      </Button>
    </div>
  )
}

export default DocsPage
